import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Z_INDEX } from '@liveHelper/constants'
import type { UIState } from '@liveHelper/models/fsm/machine'
import {
  ASPECT_RATIO_LANDSCAPE,
  ASPECT_RATIO_PORTRAIT,
  ON_CALL_FULLSCREEN_CHAT_WIDTH,
} from '@oneToOne/shared/components/chat/constants'
import { FlexCenter, Transition } from '@oneToOne/shared/styles'

import { AgoraTrackPlayer } from '@core/agoraRTC'

export const VideoCallContainer = styled.div<{ isChatExpanded: boolean }>`
  ${FlexCenter}
  ${Transition}
  position: relative;
  height: 100%;
  width: ${({ isChatExpanded }) =>
    isChatExpanded
      ? `calc(100% - ${ON_CALL_FULLSCREEN_CHAT_WIDTH}px)`
      : '100%'};
`

export const OnCallModalContainer = styled.div<{
  isMinimized: boolean
  uiState: UIState['value']
}>`
  ${Transition}
  ${FlexCenter}
  flex-grow: 1;
  width: 100%;
  position: relative;
  background: #333333; // Special color to imitate previous design
  cursor: ${({ isMinimized }) => (isMinimized ? 'pointer' : 'inherit')};
  overflow: hidden;
  ${({ isMinimized, uiState }) =>
    isMinimized || uiState === 'on_call_expanded'
      ? `border-radius: var(--border-radius-md);`
      : ''}
`

export const ButtonRow = styled.div<{ isMobileWeb: boolean }>`
  ${FlexCenter}
  position: absolute;
  bottom: var(--spacing-48);
  gap: ${({ isMobileWeb }) =>
    isMobileWeb ? 'var(--spacing-24)' : 'var(--spacing-32)'};
`

export const CloseButtonWrapperChatVersion = styled.div`
  position: absolute;
  z-index: ${Z_INDEX.FLOATING_ACTION};
  top: var(--spacing-16);
  right: var(--spacing-16);
  display: flex;
  gap: var(--spacing-8);
`

export const AgentAgoraTrackPlayer = styled(AgoraTrackPlayer)`
  position: relative;
  height: 100%;
  width: 100%;
`

export const CustomerAgoraTrackPlayer = styled(AgoraTrackPlayer)`
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: var(--border-radius-md);
`

export const CustomerAgoraTrackPlayerWrapper = styled.div<{
  isMobilePortrait: boolean
}>`
  position: absolute;
  top: var(--spacing-16);
  left: var(--spacing-16);
  border-radius: var(--border-radius-md);
  z-index: ${Z_INDEX.MAIN_VIDEO};

  height: 20%;
  aspect-ratio: ${({ isMobilePortrait }) =>
    isMobilePortrait
      ? `${ASPECT_RATIO_PORTRAIT}`
      : `${ASPECT_RATIO_LANDSCAPE}`};
  max-width: 80%;
  overflow: hidden;
`

const DesktopSizing = css`
  aspect-ratio: ${ASPECT_RATIO_LANDSCAPE};
  max-width: 100%;
  max-height: 100%;
`

export const VideoContainer = styled.div<{
  isMinimized: boolean
  isMobileWeb: boolean
  doesContainerSupportLandscape?: boolean
}>`
  position: absolute;
  overflow: hidden; // This is needed to have the video have rounded corners
  background: var(--gray-10);
  ${FlexCenter}

  ${({ isMinimized, isMobileWeb, doesContainerSupportLandscape }) =>
    isMinimized || isMobileWeb
      ? css`
          height: 100%;
          width: 100%;
          max-width: 100%;
          max-height: 100%;
        `
      : doesContainerSupportLandscape
        ? css`
            height: calc(100% - var(--spacing-16) - var(--spacing-16));
            ${DesktopSizing}
          `
        : css`
            width: calc(100% - var(--spacing-16) - var(--spacing-16));
            ${DesktopSizing}
          `}

  border-radius: ${({ isMinimized, isMobileWeb }) =>
    !isMinimized && !isMobileWeb ? 'var(--border-radius-md)' : 'inherit'};
`

export const PlayerInnerContainer = styled.div`
  ${FlexCenter}
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const VideoFireworkLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: var(--spacing-12);
`
